/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {
    BaseComponent,
    Dialog_Builder,
    DialogModule
} from "@intuitionrobotics/thunderstorm/frontend";
import {DB_Patient} from "@app/app-shared/patient";
import React from "react";
import {DialogCard} from "../../widget/DialogCard";
import {DateTimePicker} from "./DateTimePicker";
import dayjs from "../../../utils/dayjs";
import {FONTS} from "@res/fonts";
import {DateTimesUtil} from "./DateTimesUtil";
import {
    AppointmentModule,
    OnAppointmentUpdated
} from "@modules/AppointmentModule";
import {COLORS} from "@res/colors";

type Props = {
    patient: DB_Patient;
}

type State = {
    loading: boolean;
    timestamp?: number;
}

export class AppointmentDialog
    extends BaseComponent<Props, State>
    implements OnAppointmentUpdated {

    public static show(props: Props) {
        new Dialog_Builder(
            <AppointmentDialog {...props}/>
        ).setAllowIndirectClosing(true)
            .show();
    }

    private readonly minDate: Date;
    private readonly maxDate: Date;

    constructor(props: Props) {
        super(props);
        const {minDate, maxDate} = AppointmentModule.getMinMaxDateForAppointments();
        this.minDate = minDate.toDate();
        this.maxDate = maxDate.endOf("day").toDate();
        this.state = {
            loading: false,
            timestamp: this.isNewAppointment() ? undefined : AppointmentModule.getNextAppointment(this.props.patient)?.startTime
        };
    }

    __onAppointmentUpdated = () => {
        DialogModule.close();
    };

    private isNewAppointment = () => {
        return AppointmentModule.getNextAppointment(this.props.patient) === undefined;
    };

    render() {
        return (
            <DialogCard
                showX={true}
                loading={this.state.loading}
                saveLabel={this.isNewAppointment() ? "Create" : "Update"}
                cancelLabel={"Cancel"}
                title={this.isNewAppointment() ? "Create Appointment" : "Update Appointment"}
                content={this.renderContent()}
                footerExtra={
                    this.isNewAppointment() ? null : (
                        <div
                            className={"flex_center clickable"}
                            onClick={
                                () => {
                                    this.setState({loading: true});
                                    AppointmentModule.deleteAppointment(this.props.patient);
                                }
                            }
                            style={{
                                backgroundColor: COLORS.secondary_red,
                                borderRadius: 4,
                                color: COLORS.white,
                                marginLeft: "auto",
                                marginRight: "-16px",
                                minHeight: 40,
                                minWidth: 120,
                                textAlign: "center",
                                border: `1px solid ${COLORS.neutral_text_dark_2}`
                            }}
                        >
                            {FONTS.medium_16_white("Delete")}
                        </div>
                    )
                }
                onSave={() => {
                    if (this.state.timestamp === undefined)
                        return true;
                    this.setState({loading: true});
                    this.isNewAppointment()
                        ? AppointmentModule.createAppointment(this.props.patient, this.state.timestamp)
                        : AppointmentModule.updateAppointmentDateTime(this.props.patient, this.state.timestamp);
                    return true;
                }}
                saveEnabled={(this.state.timestamp !== undefined)}
            />
        );
    }

    private renderContent = () => {
        return (
            <div className={`ll_v_c`} style={{height: "40vh"}}>
                <DateTimePicker
                    _key={"appointment-date-time-picker"}
                    initialTimes={[dayjs(this.state.timestamp).toDate()]}
                    minDate={this.minDate}
                    maxDate={this.maxDate}
                    onChange={(dates) => {
                        this.setState({timestamp: (dates !== undefined) ? dayjs(dates[0]).valueOf() : undefined});
                    }}
                />
                {
                    this.state.timestamp ? (
                        <>
                            {
                                FONTS.reg_16_dark(
                                    `Appointment will be set for ${DateTimesUtil.appointmentSetPresentation(this.state.timestamp, this.props.patient.timezone)}`,
                                    {marginTop: 20, maxWidth: 300, textAlign: "center"}
                                )
                            }
                        </>
                    ) : null
                }
            </div>
        );
    };
}
